import React, { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../../lib/AuthContext'
import { countAccessibleModules } from '../../lib/permissions'
import { getUsaGreeting } from '../../lib/usaGreeting'
import { getDisplayName } from '../../lib/profileDisplayName'
import { formatRoleLabel } from '../../lib/roleLabel'
import { usePlatformOverviewData } from '../../lib/usePlatformOverviewData'
import { getVisibleOverviewWidgetIds, OVERVIEW_WIDGET_IDS } from '../../lib/dashboardWidgets'
import { formatDigestBriefingMessage, runDigestBriefing } from '../../lib/triggerDigestCron'
import { DASHBOARD_INTERNAL_APPS_NAV } from '../../lib/dashboardNav'
import OverviewHero from './OverviewHero'
import PlatformDailyReportBand from './PlatformDailyReportBand'
import AiSpotlight from './AiSpotlight'
import AiOpsLogs from './AiOpsLogs'
import LivePulse from './LivePulse'
import MainWorkspaceGrid, { InternalAppsRow } from './MainWorkspaceGrid'
import PlatformClusters from './PlatformClusters'
import OverviewEmpty from './OverviewEmpty'
import './overview.css'

export default function PlatformOverviewPage() {
  const { profile, canAccessPage, isSuperAdmin, isAdminUser, loading: authLoading } = useAuth()
  const displayName = getDisplayName(profile)
  const greeting = getUsaGreeting(displayName)
  const data = usePlatformOverviewData()
  const moduleCount = countAccessibleModules(canAccessPage)
  const visible = getVisibleOverviewWidgetIds(canAccessPage, {
    personalLibraryEnabled: data.personalLibraryEnabled,
  })
  const workspaceModuleCount = Math.max(data.modules?.length ?? 0, moduleCount)

  const [triggeringDigest, setTriggeringDigest] = useState(false)
  const [toast, setToast] = useState(null)

  const showAgents = canAccessPage('ai-agents')
  const showKnowledgeSearch = visible.has(OVERVIEW_WIDGET_IDS.knowledgeSearch)
  const showPersonalLibrary =
    visible.has(OVERVIEW_WIDGET_IDS.personalLibrary) && Boolean(data.personalLibrary)

  const showDigest = canAccessPage('ai-agents-logs')
  const showSync =
    canAccessPage('basecamp-projects') || canAccessPage('basecamp-knowledge')
  const showOpsLogs = showDigest || showSync

  const showClientKpis = canAccessPage('clients')

  useEffect(() => {
    if (!toast) return undefined
    const id = window.setTimeout(() => setToast(null), 5000)
    return () => window.clearTimeout(id)
  }, [toast])

  const handleDigest = useCallback(async () => {
    if (!isSuperAdmin || triggeringDigest) return
    setTriggeringDigest(true)
    setToast(null)
    try {
      const res = await runDigestBriefing()
      await data.refresh()
      setToast(formatDigestBriefingMessage(res))
    } catch (e) {
      setToast({ type: 'error', text: e?.message || 'Digest failed' })
    } finally {
      setTriggeringDigest(false)
    }
  }, [isSuperAdmin, data, triggeringDigest])

  if (authLoading) {
    return (
      <div className="overview-page">
        <div className="overview-skeleton" style={{ height: 120, marginBottom: 24 }} />
        <div className="overview-skeleton" style={{ height: 160, marginBottom: 20 }} />
        <div className="overview-metric-grid">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="overview-skeleton" style={{ height: 100 }} />
          ))}
        </div>
      </div>
    )
  }

  const roleLabel = formatRoleLabel(profile?.role)
  const asOfIso = data.generatedAt || data.lastUpdated?.toISOString() || null

  const agentConfigs = data.agentConfigs || []
  const activeAgentCount = agentConfigs.filter((a) => a.is_active).length
  const digestStatus =
    data.cronGroups?.[0]?.aggregateStatus || data.digestCronJob?.last_run_status || null

  return (
    <div className="overview-page">
      <OverviewHero
        greetingText={greeting.text}
        roleLabel={roleLabel}
        isAdminUser={isAdminUser}
        moduleCount={workspaceModuleCount}
        loading={data.loading}
        asOfIso={asOfIso}
        onRefresh={() => void data.refresh()}
        refreshDisabled={triggeringDigest}
      />

      {data.error ? (
        <div className="overview-error-banner" role="alert">
          <span>{data.error}</span>
          <button type="button" className="overview-btn-secondary" onClick={() => void data.refresh()}>
            Retry
          </button>
        </div>
      ) : null}

      {isSuperAdmin ? <PlatformDailyReportBand /> : null}

      {(showAgents || showKnowledgeSearch || showPersonalLibrary || showOpsLogs) ? (
        <div className="overview-ai-stack">
          <AiSpotlight
            showAgents={showAgents}
            showKnowledgeSearch={showKnowledgeSearch}
            showPersonalLibrary={showPersonalLibrary}
            agentCount={agentConfigs.length || null}
            activeAgentCount={activeAgentCount}
            digestStatus={digestStatus}
            searchableSourceCount={data.knowledgeSearch?.searchableSourceCount}
            personalLibrary={data.personalLibrary}
            loading={data.loading}
          />

          {showOpsLogs ? (
            <AiOpsLogs
              stackedUnderSpotlight={showAgents || showKnowledgeSearch || showPersonalLibrary}
              showDigest={showDigest}
              showSync={showSync}
              isSuperAdmin={isSuperAdmin}
              loading={data.loading}
              triggeringDigest={triggeringDigest}
              onRunDigest={() => void handleDigest()}
              digestCronJob={data.digestCronJob}
              cronGroups={data.cronGroups || []}
              syncCronJobs={data.syncCronJobs || []}
              syncLogs={data.syncLogs || []}
            />
          ) : null}
        </div>
      ) : null}

      <LivePulse
        showClientKpis={showClientKpis}
        clientStats={showClientKpis ? data.clientStats : null}
        raceStats={visible.has(OVERVIEW_WIDGET_IDS.raceTracker) ? data.raceStats : null}
        alertCount={visible.has(OVERVIEW_WIDGET_IDS.adimpact) ? data.alertCount : null}
        resourceCount={visible.has(OVERVIEW_WIDGET_IDS.resources) ? data.resourceCount : null}
        bplStats={visible.has(OVERVIEW_WIDGET_IDS.bigPoliticalList) ? data.bplStats : null}
        ideaBoardStats={visible.has(OVERVIEW_WIDGET_IDS.ideaBoard) ? data.ideaBoardStats : null}
        loading={data.loading}
      />

      <MainWorkspaceGrid
        canAccessPage={canAccessPage}
        data={{
          ...data,
          // Never surface client KPI payload without Clients page access
          clientStats: showClientKpis ? data.clientStats : null,
          companySplit: canAccessPage('analytics') ? data.companySplit : null,
        }}
        loading={data.loading}
      />

      <InternalAppsRow canAccessPage={canAccessPage} items={DASHBOARD_INTERNAL_APPS_NAV} />

      <PlatformClusters canAccessPage={canAccessPage} />

      {!data.loading && workspaceModuleCount <= 1 ? (
        <OverviewEmpty
          title="Limited workspace"
          message="Your account has access to few modules. This home page only shows tools granted to your role — restricted products are never listed. Use the sidebar for what you can open."
        />
      ) : null}

      {toast ? (
        <div className={`overview-toast overview-toast--${toast.type}`} role="status">
          {toast.text}
        </div>
      ) : null}
    </div>
  )
}
